import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  makeStyles,
  Typography,
  StepLabel,
  Stepper,
  Step,
} from "@material-ui/core";
import { Fragment, useState } from "react";
import { useNotify, Button, useDataProvider } from "react-admin";
import CancelIcon from "@material-ui/icons/Cancel";
import { StyledButton } from "../../../components/ListShowButton";

interface IApproveCommercialDocumentButton {
  id: number;
  status: string;
  type: "invoice" | "credit_note" | "credit_note_tagged";
}

const useStyles = makeStyles({
  dialog: {
    "& .MuiDialog-paper": {
      overflowY: "unset",
    },
  },
  cancelButton: {
    backgroundColor: "#eeeeee",
    color: "black",
  },
  table: {
    marginTop: "10px",
    padding: "1rem",
  },
});

const ApproveCommercialDocumentButton = ({
  id,
  status,
  type,
}: IApproveCommercialDocumentButton) => {
  const [open, setOpen] = useState<boolean>(false);
  const [loadingUnposted, setLoadingUnposted] = useState<boolean>(false);
  const [loadingPosted, setLoadingPosted] = useState<boolean>(false);
  const [failUnposted, setFailUnposted] = useState<boolean>(false);
  const [failPosted, setFailPosted] = useState<boolean>(false);
  const [multipleProgress, setMultipleProgress] = useState<boolean>(false);
  const [activeStep, setActiveStep] = useState(-1);

  const notify = useNotify();
  const provider = useDataProvider();
  const classes = useStyles();

  const createUnpostedDocument = (
    id: number,
    type: string,
    retryCount: number = 1
  ) => {
    provider
      .customCreate(`commercialDocuments/${id}/createOnMbc`, { data: {} })
      .then(() => {
        // credit note document with untagged invoice only have unposted document
        if (type === "credit_note") {
          setLoadingUnposted(false);
          setActiveStep(1);

          notify(`Successfully approve the document.`, "info");
          window.location.reload();
        } else if (type === "credit_note_tagged") {
          // if success create unposted document
          // then posted the credit note
          setLoadingUnposted(false);
          setActiveStep(1);
          setLoadingPosted(true);
          createPostedDocument(id);
        } else {
          // if success create Unposted document
          // then add deferral schedule item
          addDeferralSchedule(id);
        }
      })
      .catch((error: any) => {
        retryCount++;

        if (retryCount <= 3) {
          // give random delay between 2 - 3 seconds
          const delay = Math.floor(Math.random() * 1000) + 2000;
          setTimeout(() => {
            createUnpostedDocument(id, type, retryCount);
          }, delay);

          return;
        }

        notify(
          `Failed create unposted document, error: ${error.message}`,
          "warning"
        );
        setLoadingUnposted(false);
        setFailUnposted(true);
        setActiveStep(1);
        return;
      });
  };

  const addDeferralSchedule = (id: number, retryCount: number = 1) => {
    provider
      .customCreate(`commercialDocuments/${id}/addDeferralScheduleOnMbc`, {
        data: {},
      })
      .then(() => {
        setLoadingUnposted(false);
        setActiveStep(1);

        // if success create Unposted document
        // and add deferral schedule success
        // then posted the document
        setLoadingPosted(true);
        createPostedDocument(id);
      })
      .catch((error: any) => {
        retryCount++;

        if (retryCount <= 3) {
          // give random delay between 2 - 3 seconds
          const delay = Math.floor(Math.random() * 1000) + 2000;
          setTimeout(() => {
            addDeferralSchedule(id, retryCount);
          }, delay);

          return;
        }

        notify(
          `Failed create unposted document, error: ${error.message}`,
          "warning"
        );
        setLoadingUnposted(false);
        setFailUnposted(true);
        setActiveStep(1);
        return;
      });
  };

  const createPostedDocument = (id: number, retryCount: number = 1) => {
    provider
      .customCreate(`commercialDocuments/${id}/postOnMbc`, { data: {} })
      .then(() => {
        setLoadingPosted(false);
        setActiveStep(2);

        notify(`Successfully approve the document.`, "info");
        window.location.reload();
      })
      .catch((error: any) => {
        retryCount++;

        if (retryCount <= 3) {
          // give random delay between 2 - 3 seconds
          const delay = Math.floor(Math.random() * 1000) + 2000;
          setTimeout(() => {
            createPostedDocument(id, retryCount);
          }, delay);

          return;
        }

        notify(
          `Failed create posted document, error: ${error.message}`,
          "warning"
        );
        setLoadingPosted(false);
        setFailPosted(true);
        setActiveStep(2);
        return;
      });
  };

  const handleApproveCommercialDocument = async (
    id: number,
    status: string,
    type: string
  ) => {
    switch (type) {
      case "invoice":
        await setMultipleProgress(true);
        await setFailUnposted(false);
        await setFailPosted(false);
        await setActiveStep(0);
        await setLoadingUnposted(true);

        // if the document status is unposted
        // then skip the creation unposted document
        // but still need to add deferral schedule
        if (status === "unposted") {
          // add deferral schedule
          await addDeferralSchedule(id);
          return;
        }

        // create unposted document
        await createUnpostedDocument(id, type);
        break;
      case "credit_note_tagged":
        await setMultipleProgress(true);
        await setFailUnposted(false);
        await setFailPosted(false);
        await setActiveStep(0);

        // if the document status is unposted
        // then skip the creation unposted document
        if (status === "unposted") {
          setActiveStep(1);
          await setLoadingPosted(true);
          await createPostedDocument(id);
          return;
        }

        // create unposted document
        await setLoadingUnposted(true);
        await createUnpostedDocument(id, type);

        break;
      case "credit_note":
        await setFailUnposted(false);
        await setActiveStep(0);

        // create unposted document
        await setLoadingUnposted(true);
        await createUnpostedDocument(id, type);

        break;
    }
  };

  const handleOpenDialog = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Fragment>
      <StyledButton
        variant="outlined"
        onClick={handleOpenDialog}
        style={{
          color: "#4caf50",
          borderColor: "#4caf50",
        }}
      >
        Approve
      </StyledButton>
      <Dialog
        open={open}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className={classes.dialog}
        maxWidth="sm"
      >
        <DialogTitle id="alert-dialog-title">Approval Confirmation</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Are you sure want to approve this Document?
          </Typography>

          {multipleProgress && (
            <>
              <Typography>Progress :</Typography>
              <Stepper activeStep={activeStep} alternativeLabel>
                <Step key="step-key-1">
                  <StepLabel StepIconComponent={failUnposted && CancelIcon}>
                    {loadingUnposted
                      ? "Create Unposted Document (Loading...)"
                      : "Create Unposted Document"}
                  </StepLabel>
                </Step>
                <Step key="step-key-2">
                  <StepLabel StepIconComponent={failPosted && CancelIcon}>
                    {loadingPosted
                      ? "Create Posted Document (Loading...)"
                      : "Create Posted Document"}
                  </StepLabel>
                </Step>
              </Stepper>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => handleApproveCommercialDocument(id, status, type)}
            color="primary"
            undoable={false}
            label={loadingUnposted || loadingPosted ? `Loading...` : `Yes`}
            disabled={loadingUnposted || loadingPosted}
          />
          <Button
            onClick={handleClose}
            color="secondary"
            label="Cancel"
            className={classes.cancelButton}
            disabled={loadingUnposted || loadingPosted}
          />
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

export default ApproveCommercialDocumentButton;
